import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import {
  fetchReviewQueue,
  fetchReviewQueueEntry,
  fetchReviewQueuePendingCount,
  retryReviewQueueItem,
  ReviewQueueEntry,
} from 'api/DataReview';

const PAGE_SIZE = 25;
// Two polling cadences:
// - Background (pill badge) refresh while a Data Review session is open, so
//   the pending-count badge stays roughly current even when the drawer is
//   closed. Cheap and channel-scoped.
// - Faster cadence while the drawer is OPEN AND has at least one Pending /
//   In Progress row, so the user watching a running task sees it transition
//   without clicking refresh. Auto-stops when everything is terminal.
const PILL_REFRESH_MS = 20000;
const DRAWER_REFRESH_MS = 5000;
// refresh() refetches the whole currently-loaded window (not just page 1).
// If it shrank the list back to one page while the user was scrolled deep,
// the drawer's IntersectionObserver sentinel came into view and chained
// loadMore() calls page-by-page every poll tick — an infinite request loop
// with no user interaction. The cap keeps the poll request bounded on very
// deep scrolls; rows beyond it are preserved client-side instead.
const REFRESH_WINDOW_MAX = 500;

interface UseReviewQueueResult {
  entries: ReviewQueueEntry[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  hasMore: boolean;
  refreshEntry: (uid: string) => Promise<void>;
  retry: (uid: string) => Promise<void>;
  pendingCount: number;
}

/**
 * Fetches the Review Queue for a channel and exposes the count of
 * (Pending + In Progress) entries for the header pill badge.
 *
 * Pagination: the drawer pulls page-by-page (PAGE_SIZE = 25) via
 * `loadMore()`, wired to an IntersectionObserver sentinel at the bottom
 * of the queue table. `refresh()` refetches the currently-loaded window
 * from skip=0 (capped at REFRESH_WINDOW_MAX) and is also called after a
 * dispatch so newly-queued rows appear immediately — it must never shrink
 * the list below what's loaded, or the sentinel refires in a loop.
 */
export function useReviewQueue(
  csKey: string | undefined,
  drawerOpen: boolean = false,
): UseReviewQueueResult {
  const [entries, setEntries] = useState<ReviewQueueEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Authoritative pending count from the dedicated count endpoint. The
  // entries-derived fallback only sees page 1 (25 rows), so on a busy
  // channel it would underestimate — the dedicated count covers all
  // pages with a single index-only Mongo query.
  const [serverPendingCount, setServerPendingCount] = useState<number | null>(null);
  const inFlight = useRef(false);
  // Mirror of entries.length for refresh() — read via a ref so refresh's
  // useCallback deps stay [csKey] and the 5s polling interval isn't torn
  // down and recreated on every page load.
  const entriesLenRef = useRef(0);

  useEffect(() => {
    entriesLenRef.current = entries.length;
  }, [entries]);

  const refreshPendingCount = useCallback(async () => {
    if (!csKey) return;
    try {
      const res = await fetchReviewQueuePendingCount(csKey);
      if (typeof res?.pending_count === 'number') {
        setServerPendingCount(res.pending_count);
      }
    } catch (_e: any) {
      // Pill polling is best-effort — never surface as a blocking error
      // (the drawer's `error` state is reserved for the list endpoint).
    }
  }, [csKey]);

  const refresh = useCallback(async () => {
    if (!csKey || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      // Parallel: list (for the drawer rows) + dedicated count (for the
      // pill). The count covers all pages so the badge stays accurate
      // even when there are more pending tasks than fit in the window.
      // The list request covers every already-loaded row (capped) so a
      // background poll never collapses a deep-scrolled list to page 1.
      const windowLimit = Math.min(
        Math.max(PAGE_SIZE, entriesLenRef.current),
        REFRESH_WINDOW_MAX,
      );
      const [listRes, countRes] = await Promise.all([
        fetchReviewQueue(csKey, windowLimit, 0),
        fetchReviewQueuePendingCount(csKey).catch(() => null),
      ]);
      const next = listRes?.data || [];
      setEntries((prev) => {
        if (prev.length <= next.length) return next;
        // The cap truncated the refetch (or the server returned fewer
        // rows): keep the stale tail instead of shrinking the list, so
        // the scroll position holds and the sentinel doesn't refire.
        const seen = new Set(next.map((e) => e.uid));
        return [...next, ...prev.slice(next.length).filter((e) => !seen.has(e.uid))];
      });
      setTotal(typeof listRes?.total === 'number' ? listRes.total : (listRes?.data || []).length);
      if (countRes && typeof countRes.pending_count === 'number') {
        setServerPendingCount(countRes.pending_count);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch review queue');
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [csKey]);

  const loadMore = useCallback(async () => {
    if (!csKey || inFlight.current || loadingMore) return;
    if (entries.length >= total && total > 0) return;
    inFlight.current = true;
    setLoadingMore(true);
    try {
      const res = await fetchReviewQueue(csKey, PAGE_SIZE, entries.length);
      const next = res?.data || [];
      // Dedup by uid in case a refresh races with a load-more.
      setEntries((prev) => {
        const seen = new Set(prev.map((e) => e.uid));
        return [...prev, ...next.filter((e) => !seen.has(e.uid))];
      });
      if (typeof res?.total === 'number') setTotal(res.total);
    } catch (e: any) {
      setError(e?.message || 'Failed to load more queue entries');
    } finally {
      setLoadingMore(false);
      inFlight.current = false;
    }
  }, [csKey, entries.length, loadingMore, total]);

  // Per-entry refresh — used by the View Log panel's refresh-logs icon.
  // Replaces the matching row in `entries` so a panel that's open on this
  // uid re-renders with the freshest logs without re-pulling the whole list.
  const refreshEntry = useCallback(
    async (uid: string) => {
      if (!csKey || !uid) return;
      try {
        const res = await fetchReviewQueueEntry(csKey, uid);
        const fresh = res?.data;
        if (!fresh) return;
        setEntries((prev) => {
          const idx = prev.findIndex((e) => e.uid === uid);
          if (idx < 0) return [fresh, ...prev];
          const copy = prev.slice();
          copy[idx] = fresh;
          return copy;
        });
      } catch (e: any) {
        setError(e?.message || 'Failed to refresh queue entry');
      }
    },
    [csKey],
  );

  const retry = useCallback(
    async (uid: string) => {
      if (!csKey) return;
      try {
        const res = await retryReviewQueueItem(csKey, uid);
        if (res?.done) {
          toast.success('Task added to queue');
          // Optimistic refresh so the new Pending row appears immediately.
          refresh();
        } else {
          toast.error(res?.message || 'Failed to retry task');
        }
      } catch (e: any) {
        toast.error(e?.message || 'Failed to retry task');
      }
    },
    [csKey, refresh],
  );

  // Auto-fetch once on first mount when csKey is available.
  useEffect(() => {
    if (csKey) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csKey]);

  // Prefer the server-side count (covers all pages); fall back to the
  // entries-derived count when the dedicated endpoint hasn't responded
  // yet (first paint).
  const pendingCount =
    serverPendingCount !== null
      ? serverPendingCount
      : entries.filter((e) => e.status === 'Pending' || e.status === 'In Progress').length;

  const hasMore = total > entries.length;
  // Derived once per render so the polling effect's dependency array
  // only flips when the value actually changes — otherwise every
  // refresh() would tear down + recreate the interval.
  const hasPending = pendingCount > 0;

  // Background pill refresh — keeps pendingCount current even when the
  // drawer is closed. Hits the cheap count-only endpoint (no row data,
  // single index-only Mongo query) so a busy channel doesn't pull 25 KB
  // every 20s just to compute one number.
  useEffect(() => {
    if (!csKey) return undefined;
    // Prime once on mount/csKey-change so the badge is correct before
    // the first interval fires.
    refreshPendingCount();
    const id = setInterval(() => {
      refreshPendingCount();
    }, PILL_REFRESH_MS);
    return () => clearInterval(id);
  }, [csKey, refreshPendingCount]);

  // Faster drawer-open polling — only while the drawer is open AND there
  // is at least one Pending/In Progress row to watch. Auto-stops when the
  // queue is fully terminal, so an idle drawer doesn't hammer the BE.
  useEffect(() => {
    if (!csKey || !drawerOpen || !hasPending) return undefined;
    const id = setInterval(() => {
      refresh();
    }, DRAWER_REFRESH_MS);
    return () => clearInterval(id);
  }, [csKey, drawerOpen, hasPending, refresh]);

  return {
    entries,
    loading,
    loadingMore,
    error,
    refresh,
    loadMore,
    hasMore,
    refreshEntry,
    retry,
    pendingCount,
  };
}
